import { FC } from 'react';
import { alpha, Box, CircularProgress, IconButton, LinearProgress, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { Trans, useTrans } from '@libs/i18n';
import { FileUploadType } from '@libs/types';
import { formatFileSize } from '@libs/utils';
import { ArrowClockwise, ArrowCounterClockwise, Clock, CloudCheck, TrashSimple, X } from '@phosphor-icons/react';
import { NoFlipPaper } from '@utils';

import { TooltipCard } from '../Tooltip';

export interface FileUploadItemProps {
  file: FileUploadType;
  layout: 'small' | 'large';
  onDelete?: (fileId: string) => void;
  onCancel?: (fileId: string) => void;
  onUndoDelete?: (fileId: string) => void;
  onRetryDelete?: (fileId: string) => void;
  onRetryUpload?: (fileId: string) => void;
  onRemoveFailedUpload?: (fileId: string) => void;
  disabled?: boolean;
  /** When true, overlays the card with a "moving…" loading state. */
  isMoving?: boolean;
  /** When true, the delete/trash button is disabled for this specific file. */
  deleteDisabled?: boolean;
  /** Optional render function for additional content below the file item */
  renderExtraContent?: (file: FileUploadType) => React.ReactNode;
  /** Optional render function for an inline badge in the meta row (large layout). */
  renderFileBadge?: (file: FileUploadType) => React.ReactNode;
}

export const FileUploadItem: FC<FileUploadItemProps> = observer(
  ({ file, layout, onDelete, onCancel, onUndoDelete, onRetryDelete, onRetryUpload, onRemoveFailedUpload, disabled = false, isMoving = false, deleteDisabled = false, renderExtraContent, renderFileBadge }) => {
    const theme = useTheme();
    const { dir } = useDirection();
    const { t } = useTrans();

    const fileName = file.fileName;

    // Loading overlay shown while this document is being moved to another
    // domain. Covers the whole card (file row + classification badge).
    const movingOverlay = isMoving ? (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.7),
          backdropFilter: 'blur(1px)',
        }}
        aria-live="polite"
      >
        <CircularProgress size={20} thickness={5} />
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {t('app.assessment.classification.suggestion.moving')}
        </Typography>
      </Box>
    ) : null;

    const handleAction = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (file.status === 'uploading') {
        onCancel?.(file.id);
      } else if (file.status === 'queued') {
        onCancel?.(file.id);
      } else if (file.status === 'pendingDelete') {
        onUndoDelete?.(file.id);
      } else if (file.status === 'deleted') {
        // Don't show undo button for deleted files - they're permanently deleted
        return;
      } else if (file.status === 'deleteFailed') {
        onRetryDelete?.(file.id);
      } else if (file.status === 'notSaved' || file.status === 'uploaded') {
        // Can delete notSaved and uploaded files
        onDelete?.(file.id);
      }
    };

    const getStatusTranslationKey = () => {
      if (file.status === 'queued') {
        return 'fileUpload.status.queued' as const;
      }
      if (file.status === 'uploading') {
        return 'fileUpload.status.uploading' as const;
      }
      if (file.status === 'uploaded') {
        return 'fileUpload.status.uploaded' as const;
      }
      if (file.status === 'failed') {
        return 'fileUpload.status.failed' as const;
      }
      if (file.status === 'pendingDelete') {
        return 'fileUpload.status.pendingDelete' as const;
      }
      if (file.status === 'deleting') {
        return 'fileUpload.status.deleting' as const;
      }
      if (file.status === 'deleted') {
        return 'fileUpload.status.deleted' as const;
      }
      if (file.status === 'deleteFailed') {
        return 'fileUpload.status.deleteFailed' as const;
      }
      return 'fileUpload.status.queued' as const;
    };

    if (layout === 'large') {
      return (
        <NoFlipPaper
          mode={dir}
          variant="outlined"
          sx={{
            position: 'relative',
            // Fills the grid cell so a row of cards shares one height. Nothing
            // else changes: same padding, border, radius and colours.
            height: '100%',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            backgroundColor: file.status === 'deleted'
              ? 'action.hover'
              : file.status === 'queued'
                ? 'rgba(0, 195, 240, 0.04)'
                : 'background.paper',
            borderRadius: 2,
            opacity: file.status === 'deleted' ? 0.7 : 1,
            transition: 'all 0.3s ease',
            borderStyle: file.status === 'queued' ? 'dashed' : 'solid',
            borderWidth: file.status === 'queued' ? '2px' : '1px',
            borderColor: file.status === 'queued'
              ? 'info.main'
              : file.status === 'deleted'
                ? 'action.disabled'
                : undefined,
          }}
          role="listitem"
          aria-label={t('fileUpload.ariaLabel.file', { fileName })}
        >
          {movingOverlay}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <TooltipCard variant="dark" description={fileName} placement="top" enterDelay={500} arrow>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    sx={{
                      color: file.status === 'deleted'
                        ? 'text.secondary'
                        : file.status === 'queued'
                          ? 'info.main'
                          : 'text.primary',
                      textDecoration: file.status === 'deleted' ? 'line-through' : 'none',
                      fontStyle: file.status === 'queued' ? 'italic' : 'normal',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {fileName}
                  </Typography>
                </TooltipCard>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: file.status === 'failed' || file.status === 'deleteFailed'
                      ? 'error.main'
                      : file.status === 'pendingDelete'
                        ? 'warning.main'
                        : file.status === 'deleting'
                          ? 'warning.main'
                          : file.status === 'deleted'
                            ? 'text.secondary'
                            : file.status === 'queued'
                              ? 'info.main'
                              : 'primary.main',
                    opacity: file.status === 'deleted' ? 0.6 : 1,
                    fontWeight: file.status === 'queued' ? 600 : 400,
                  }}
                >
                  <Trans i18nKey={getStatusTranslationKey()} />
                  {file.status === 'pendingDelete' && file.deleteTimer !== undefined && (
                    <> ({file.deleteTimer}s)</>
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                    opacity: file.status === 'deleted' ? 0.4 : 0.6
                  }}
                >
                  -
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: file.status === 'queued' ? 'info.main' : 'text.primary',
                    opacity: file.status === 'deleted' ? 0.6 : 0.8,
                    fontWeight: file.status === 'queued' ? 500 : 400,
                  }}
                >
                  {formatFileSize(file.size)}
                </Typography>
                {renderFileBadge?.(file)}
              </Box>
              {/* Error Message */}
              {file.status === 'failed' && (file.errorMessage || file.error) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'error.main',
                    fontWeight: 500,
                    mt: 1,
                    display: 'block',
                    wordBreak: 'break-word',
                  }}
                >
                  {file.errorMessage || file.error}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              {(file.status === 'uploading' || file.status === 'uploaded') && (
                <Box
                  sx={{ width: 80 }}
                  role="progressbar"
                  aria-valuenow={file.progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  {file.status === 'uploading' && (
                    <Typography
                      variant="body2"
                      sx={{ color: 'primary.main', fontWeight: 500, fontSize: '0.75rem', textAlign: 'center', mb: 0.5 }}
                    >
                      {Math.round(file.progress)}%
                    </Typography>
                  )}
                  <LinearProgress
                    variant="determinate"
                    value={file.status === 'uploaded' ? 100 : file.progress}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: 'grey.700',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: file.status === 'uploaded' || file.progress === 100
                          ? theme.palette.success.main
                          : theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                {file.status === 'queued' && (
                  <TooltipCard variant="dark" description="File is queued and waiting to be uploaded" arrow>
                    <Box component="span" sx={{ display: 'flex' }}>
                      <Clock
                        size={20}
                        weight="regular"
                        color={theme.palette.info.main}
                        aria-label={t('fileUpload.ariaLabel.preparing')}
                      />
                    </Box>
                  </TooltipCard>
                )}
                {file.status === 'uploaded' && (
                  <TooltipCard variant="dark" description="File successfully uploaded and saved" arrow>
                    <Box component="span" sx={{ display: 'flex' }}>
                      <CloudCheck
                        size={20}
                        weight="thin"
                        color={theme.palette.success.main}
                        aria-label={t('fileUpload.ariaLabel.uploaded')}
                      />
                    </Box>
                  </TooltipCard>
                )}
                {/* Show countdown timer for pending deletion */}
                {file.status === 'pendingDelete' && file.deleteTimer !== undefined && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'warning.main',
                      fontWeight: 600,
                      minWidth: '28px',
                      textAlign: 'center',
                    }}
                  >
                    {file.deleteTimer}
                  </Typography>
                )}

                {/* Show loading indicator when deleting */}
                {file.status === 'deleting' && (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: 'warning.main',
                    }}
                    aria-label={t('fileUpload.ariaLabel.deleting', { fileName })}
                  />
                )}

                {/* Only show undo button for pending deletion, NOT for deleted files */}
                {file.status === 'pendingDelete' && onUndoDelete && (
                  <TooltipCard variant="dark" description="Cancel deletion — keep this file" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUndoDelete(file.id);
                      }}
                      disabled={disabled}
                      aria-label={t('fileUpload.ariaLabel.cancelDeletion', { fileName })}
                      sx={{ padding: 0.5 }}
                    >
                      <ArrowCounterClockwise size={20} color={theme.palette.primary.main} />
                    </IconButton>
                  </TooltipCard>
                )}
                {file.status === 'deleteFailed' && onRetryDelete && (
                  <TooltipCard variant="dark" description="Retry deletion" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetryDelete(file.id);
                      }}
                      disabled={disabled}
                      aria-label={t('fileUpload.ariaLabel.retryDeletion', { fileName })}
                      sx={{ padding: 0.5 }}
                    >
                      <ArrowClockwise size={20} color={theme.palette.error.main} />
                    </IconButton>
                  </TooltipCard>
                )}
                {/* Show retry and remove buttons for failed uploads */}
                {file.status === 'failed' && onRetryUpload && (
                  <TooltipCard variant="dark" description="Retry upload" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetryUpload(file.id);
                      }}
                      disabled={disabled}
                      aria-label={t('fileUpload.ariaLabel.retryUpload', { fileName })}
                      sx={{ padding: 0.5 }}
                    >
                      <ArrowClockwise size={20} color={theme.palette.error.main} />
                    </IconButton>
                  </TooltipCard>
                )}
                {file.status === 'failed' && onRemoveFailedUpload && (
                  <TooltipCard variant="dark" description="Remove this failed upload" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveFailedUpload(file.id);
                      }}
                      disabled={disabled}
                      aria-label={t('fileUpload.ariaLabel.removeFailedUpload', { fileName })}
                      sx={{ padding: 0.5 }}
                    >
                      <TrashSimple size={20} color={theme.palette.error.main} />
                    </IconButton>
                  </TooltipCard>
                )}
                {/* Show cancel button only for queued files, NOT for uploading/notSaved */}
                {file.status === 'queued' && (
                  <TooltipCard variant="dark" description="Cancel upload" arrow>
                    <IconButton
                      size="small"
                      onClick={handleAction}
                      disabled={disabled}
                      aria-label={t('fileUpload.ariaLabel.cancelUpload', { fileName })}
                      sx={{ padding: 0.5 }}
                    >
                      <X size={20} color={theme.palette.error.main} />
                    </IconButton>
                  </TooltipCard>
                )}

                {/* Show delete button for uploaded/notSaved files */}
                {(file.status === 'uploaded' || file.status === 'notSaved') && (
                  <TooltipCard variant="dark" description={deleteDisabled ? '' : 'Delete this file'} arrow>
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleAction}
                          disabled={disabled || deleteDisabled}
                          aria-label={t('fileUpload.ariaLabel.delete', { fileName })}
                          sx={{ padding: 0.5 }}
                        >
                          <TrashSimple size={20} color={deleteDisabled ? theme.palette.action.disabled : theme.palette.error.main} />
                        </IconButton>
                      </span>
                    </TooltipCard>
                )}
              </Box>
          </Box>
          </Box>
          {/* Render extra content if provided (e.g., classification badge) */}
          {renderExtraContent && renderExtraContent(file)}
        </NoFlipPaper>
      );
    }

    // Small layout
    return (
      <Box
        sx={{
          position: 'relative',
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          backgroundColor: file.status === 'deleted'
            ? 'action.hover'
            : file.status === 'queued'
              ? 'rgba(0, 195, 240, 0.04)'
              : 'transparent',
          borderRadius: 2,
          border: file.status === 'queued' ? '2px dashed' : 'none',
          borderColor: file.status === 'queued' ? 'info.main' : 'transparent',
          width: '100%',
          minWidth: 0,
          opacity: file.status === 'deleted' ? 0.7 : 1,
          transition: 'all 0.3s ease',
        }}
        role="listitem"
        aria-label={t('fileUpload.ariaLabel.file', { fileName })}
      >
        {movingOverlay}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            flexShrink: 0,
            flex: 1,
            minWidth: 0,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: '0 1 auto', minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <TooltipCard variant="dark" description={fileName} placement="top" enterDelay={500} arrow>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  sx={{
                    color: file.status === 'deleted'
                      ? 'text.secondary'
                      : file.status === 'queued'
                        ? 'info.main'
                        : 'text.primary',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    textDecoration: file.status === 'deleted' ? 'line-through' : 'none',
                    fontStyle: file.status === 'queued' ? 'italic' : 'normal',
                  }}
                >
                  {fileName}
                </Typography>
              </TooltipCard>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: file.status === 'failed'
                  ? 'error.main'
                  : file.status === 'queued'
                    ? 'info.main'
                    : file.status === 'deleting'
                      ? 'warning.main'
                      : file.status === 'deleted'
                        ? 'text.secondary'
                        : 'primary.main',
                opacity: file.status === 'deleted' ? 0.6 : 1,
                whiteSpace: 'nowrap',
                fontWeight: file.status === 'queued' ? 600 : 400,
                mt: 0.25, // Slight offset to align with name text baseline
              }}
            >
              <Trans i18nKey={getStatusTranslationKey()} />
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: file.status === 'queued' ? 'info.main' : 'primary.main',
                opacity: file.status === 'deleted' ? 0.6 : 0.8,
                whiteSpace: 'nowrap',
                fontWeight: file.status === 'queued' ? 500 : 400,
                mt: 0.25,
              }}
            >
              {formatFileSize(file.size)}
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {file.status === 'queued' && (
              <Clock
                size={16}
                weight="regular"
                color={theme.palette.info.main}
                aria-label={t('fileUpload.ariaLabel.preparing')}
              />
            )}
            {/* Show loading indicator when deleting */}
            {file.status === 'deleting' && (
              <CircularProgress
                size={16}
                sx={{
                  color: 'warning.main',
                }}
                aria-label={t('fileUpload.ariaLabel.deleting', { fileName })}
              />
            )}
            {file.status === 'deleted' && onUndoDelete && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onUndoDelete(file.id);
                }}
                disabled={disabled}
                aria-label={t('fileUpload.ariaLabel.undoDeletion', { fileName })}
                sx={{
                  padding: 0.5
                }}
              >
                <ArrowCounterClockwise size={16} color={theme.palette.primary.main} />
              </IconButton>
            )}
            {file.status === 'deleteFailed' && onRetryDelete && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetryDelete(file.id);
                }}
                disabled={disabled}
                aria-label={t('fileUpload.ariaLabel.retryDeletion', { fileName })}
                sx={{
                  padding: 0.5
                }}
              >
                <ArrowClockwise size={16} color={theme.palette.error.main} />
              </IconButton>
            )}
            {file.status === 'failed' && onRetryUpload && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetryUpload(file.id);
                }}
                disabled={disabled}
                aria-label={t('fileUpload.ariaLabel.retryUpload', { fileName })}
                sx={{
                  padding: 0.5
                }}
              >
                <ArrowClockwise size={16} color={theme.palette.error.main} />
              </IconButton>
            )}
            {file.status === 'failed' && onRemoveFailedUpload && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFailedUpload(file.id);
                }}
                disabled={disabled}
                aria-label={t('fileUpload.ariaLabel.removeFailedUpload', { fileName })}
                sx={{
                  padding: 0.5
                }}
              >
                <TrashSimple size={16} color={theme.palette.error.main} />
              </IconButton>
            )}
            {file.status !== 'deleted' && file.status !== 'deleteFailed' && file.status !== 'deleting' && file.status !== 'failed' && (
              <span>
                <IconButton
                  size="small"
                  onClick={handleAction}
                  disabled={disabled || (deleteDisabled && file.status !== 'uploading' && file.status !== 'queued')}
                  aria-label={
                    file.status === 'uploading' || file.status === 'queued'
                      ? t('fileUpload.ariaLabel.cancelUpload', { fileName })
                      : t('fileUpload.ariaLabel.delete', { fileName })
                  }
                  sx={{ padding: 0.5 }}
                >
                  {file.status === 'uploading' || file.status === 'queued' ? (
                    <X size={16} color={theme.palette.error.main} />
                  ) : (
                    <TrashSimple size={16} color={deleteDisabled ? theme.palette.action.disabled : theme.palette.error.main} />
                  )}
                </IconButton>
              </span>
            )}
          </Box>
          {/* Error Message */}
          {file.status === 'failed' && (file.errorMessage || file.error) && (
            <Typography
              variant="caption"
              sx={{
                color: 'error.main',
                fontWeight: 500,
                mt: 0.5,
                display: 'block',
                wordBreak: 'break-word',
              }}
            >
              {file.errorMessage || file.error}
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
);
